import { defineStore } from "pinia"
import { StandardPlace } from "./ResearchDataModel"
import { useResearchDataApi } from "./ResearchDataApi"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"


interface PlaceMatchResult {
    name: string
    matches: PlaceMatch[]
}

interface PlaceMatch {
    place: StandardPlace
    confidence: number
}

export const useStandardPlaceStore = defineStore("standardPlaceStore", () => {
    const places = new Map<string, StandardPlace>()
    const placesByName = new Map<string, PlaceMatch>()

    const rd = useResearchDataApi()

    async function getBestPlacesAsync(names: (string | undefined)[]) {
        const store = useStandardPlaceStore() // use to get/return reactive store objects
        const definedNames = names.filter(isDefined)
        const notFoundNames = definedNames.filter(n => !store.placesByName.has(n))
        if (notFoundNames.length) {
            const plainResults = (await rd.postPlainAsync("standardPlaces/$lookup", notFoundNames)) as any[]
            addMatchesToStore(plainResults.map(r => plainToMatchResult(r)))
        }
        return names.map(getBestLoadedPlace).filter(isDefined)
            .sort((a, b) => b.confidence - a.confidence) // highest first
    }

    function getBestLoadedPlace(name: string | undefined, minConfidence = 0.5) {
        const store = useStandardPlaceStore() // use to get/return reactive store objects
        let partialName = name
        while (partialName) {
            const match = store.placesByName.get(partialName)
            if (match && match.confidence >= minConfidence)
                return match

            // remove the first (lowest) level and any adjacent empty levels and try again
            do {
                const delimIndex = partialName.indexOf(',')
                if (delimIndex < 0)
                    return undefined // nothing left to try

                let startIndex = delimIndex + 1
                while (partialName.startsWith(' ', startIndex)) {
                    startIndex++
                }
                partialName = partialName.substring(startIndex)
            } while (partialName.startsWith(','))
        }
        return undefined // not found
    }

    function addMatchesToStore(results: PlaceMatchResult[]) {
        const store = useStandardPlaceStore() // use to get/return reactive store objects
        for (const r of results) {
            const m = r.matches[0]
            if (!store.places.has(m.place.id)) {
                store.places.set(m.place.id, m.place)
            }
            if (!store.placesByName.has(r.name)) {
                store.placesByName.set(r.name, m)
            }
        }
    }

    return {
        places,
        placesByName,

        getBestPlacesAsync,
        getBestLoadedPlace,
    }
})

function plainToMatchResult(p: any) {
    const result = p as PlaceMatchResult
    for (const m of result.matches) {
        if (m.place) {
            m.place = plainToStandardPlace(m.place)
        }
    }
    return result
}

function plainToStandardPlace(p: any) {
    const place = assignExisting(new StandardPlace(), p)
    return place
}
